import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PersonalInfoRequest } from '@/types';
import { FilePreview } from './FilePreview';
import { createBlobUrl } from '@/utils/files/conversion';

interface PersonalInfoRequestDialogProps {
  request: PersonalInfoRequest;
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function PersonalInfoRequestDialog({
  request,
  open,
  onClose,
  onApprove,
  onReject,
}: PersonalInfoRequestDialogProps) {
  const photoUrl = request.personalInfo.photoUrl ? createBlobUrl(request.personalInfo.photoUrl) : null;
  const resumeUrl = request.personalInfo.resumeUrl ? createBlobUrl(request.personalInfo.resumeUrl) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>مراجعة طلب إضافة معلومات</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-6">
            {/* Requester Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">معلومات مقدم الطلب</h3>
              <p className="text-sm text-gray-600">الاسم: {request.requesterName}</p>
              <p className="text-sm text-gray-600">رقم الهاتف: {request.phoneNumber}</p>
            </div>

            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-semibold">المعلومات المطلوب إضافتها</h3>
              <div className="bg-white p-4 rounded-lg border space-y-4">
                <p className="font-medium">{request.personalInfo.fullName}</p>
                <p className="text-gray-600">{request.personalInfo.description}</p>
                
                {/* Photo Preview */}
                {photoUrl && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">الصورة الشخصية:</h4>
                    <div className="w-32 h-32 rounded-lg overflow-hidden border">
                      <img 
                        src={photoUrl}
                        alt={request.personalInfo.fullName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Resume Preview */}
                {resumeUrl && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">السيرة الذاتية:</h4>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <a 
                        href={resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        عرض ملف السيرة الذاتية
                      </a>
                    </div>
                    {request.personalInfo.isResumePublic && (
                      <p className="text-sm text-gray-500">
                        * سيكون هذا الملف متاحاً للجميع
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 gap-2">
          <Button onClick={onApprove} className="hover:bg-secondary">
            قبول الطلب
          </Button>
          <Button 
            variant="destructive" 
            onClick={onReject}
          >
            رفض الطلب
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
          >
            إغلاق
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}